import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import React from "react";
import Skeleton from "react-loading-skeleton";
import { Link } from "react-router-dom";

import { Authorizations } from "~/api/types";
import { canCollectionMember } from "~/helpers/authorizations";

import { Domain } from "../../api/Api";
import {
  Pagination,
  PaginationConfig,
} from "../../components/ui/pagination/Pagination";
import { SearchBar } from "../ui/search/SearchBar";
import ActionsSiteSpeed from "./ActionsSiteSpeed";
import styles from "./SiteSpeedTable.module.scss";
import type { MemberAuthorizations } from "./types";
import { globalVisibilityEngineRoutes } from "./useVisibilityEngineRoutes";

interface SiteSpeedTableProps {
  data: Domain[];
  pagination: PaginationConfig;
  setPagination: (pagination: PaginationConfig) => void;
  isLoading: boolean;
  setSearchTerm: (searchTerm: string) => void;
  searchTerm: string;
  authorizations?: MemberAuthorizations;
  isFetching?: boolean;
}

const renderWithSkeleton = (isLoading: boolean, content: React.ReactNode) =>
  isLoading ? <Skeleton className={styles.skeletonCell} /> : content;

const renderStatusCell = (state: string) => {
  if (state === "ready")
    return <span className={styles.statusReady}>Ready</span>;
  if (state === "completed")
    return <span className={styles.statusReady}>Completed</span>;
  if (state === "updating")
    return <span className={styles.statusPending}>Updating</span>;
  if (state === "pending")
    return <span className={styles.statusPending}>Pending</span>;
  if (state === "error")
    return <span className={styles.statusError}>Error</span>;

  return <span className={styles.statusUnknown}>{state || "Unknown"}</span>;
};

const SiteSpeedTable: React.FC<SiteSpeedTableProps> = ({
  data,
  pagination,
  setPagination,
  isLoading,
  searchTerm,
  setSearchTerm,
  authorizations,
  isFetching,
}) => {
  // Domain links always resolve to the global module: a listed domain may
  // belong to a different site than the one in the current URL, so keeping the
  // site context would produce an inconsistent `/sites/:id/...` link.
  const veRoutes = globalVisibilityEngineRoutes;

  const handlePageChange = (page: number) =>
    setPagination({ ...pagination, currentPage: page });
  const handleItemsPerPageChange = (items: number) =>
    setPagination({ ...pagination, itemsPerPage: items, currentPage: 1 });

  const columnHelper = createColumnHelper<Domain>();

  const columns = [
    columnHelper.accessor("name", {
      header: "Domain",
      cell: (info) => {
        const index = info.row.index;
        const auth = authorizations as unknown as Authorizations;
        const canShow = canCollectionMember(auth, index, "show");
        const canUpdate = canCollectionMember(auth, index, "update");
        const hasPermissions = canShow && canUpdate;
        const step = info.row.original?.steps_incomplete?.[0];

        const integrationType = info.row.original?.integration_type;

        let url;
        if (!integrationType) {
          url = veRoutes.wizardStep(info.row.original.id, "setup");
        } else {
          url = step
            ? veRoutes.wizardStep(info.row.original.id, step)
            : veRoutes.domain(info.row.original.id);
        }

        const domain = hasPermissions ? (
          <Link to={url}>{info.getValue()}</Link>
        ) : (
          info.getValue()
        );

        return renderWithSkeleton(isLoading, domain);
      },
      size: 600,
    }),
    columnHelper.accessor("verification_state", {
      id: "verification_state",
      header: "Verification",
      cell: (info) =>
        renderWithSkeleton(isLoading, renderStatusCell(info.getValue() || "")),
    }),
    columnHelper.accessor("acme_status", {
      id: "acme_state",
      header: "Certificate",
      cell: (info) => {
        const integrationType = info.row.original?.integration_type;
        const isUnavailable =
          integrationType === "edge" || integrationType === "plugin";
        const content = isUnavailable ? (
          <span className={styles.statusDisabled}>Unavailable</span>
        ) : (
          renderStatusCell(info.getValue() || "")
        );

        return renderWithSkeleton(isLoading, content);
      },
    }),
    columnHelper.accessor("ns_state", {
      id: "ns_state",
      header: "Routing",
      cell: (info) =>
        renderWithSkeleton(isLoading, renderStatusCell(info.getValue() || "")),
    }),
    columnHelper.display({
      id: "actions",
      header: "",
      cell: (info) => {
        const index = info.row.index;
        const domain = info.row.original;
        const step = domain?.steps_incomplete?.[0];
        const url = step
          ? veRoutes.wizardStep(info.row.original.id, step)
          : veRoutes.domain(info.row.original.id);

        return (
          <ActionsSiteSpeed
            index={index}
            authorizations={authorizations}
            domain={domain}
            url={url}
          />
        );
      },
    }),
  ];

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    columnResizeMode: "onChange",
  });

  return (
    <>
      <div className={styles.siteSpeedTable}>
        <div className={styles.siteSpeedHeader}>
          <SearchBar
            onChange={setSearchTerm}
            value={searchTerm}
            placeholder="Search"
          />
        </div>
        <table className={styles.siteSpeedTableTable}>
          <thead>
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    style={{ width: header.column.getSize() }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {isLoading
              ? Array.from({ length: 10 }).map((_, index) => (
                  <tr key={index}>
                    {columns.map((_, colIndex) => (
                      <td key={colIndex} className={styles.skeletonCell}>
                        <Skeleton className={styles.skeletonCell} />
                      </td>
                    ))}
                  </tr>
                ))
              : table?.getRowModel()?.rows?.map((row) => (
                  <tr key={row?.id}>
                    {row?.getVisibleCells()?.map((cell) => (
                      <td
                        key={cell.id}
                        style={{ width: cell.column.getSize() }}
                      >
                        {flexRender(
                          cell?.column?.columnDef?.cell,
                          cell?.getContext(),
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
          </tbody>
        </table>
        <Pagination
          {...pagination}
          isLoading={isFetching}
          totalItems={pagination.totalItems}
          onPageChange={handlePageChange}
          onItemsPerPageChange={handleItemsPerPageChange}
        />
      </div>
    </>
  );
};

export default SiteSpeedTable;
